import React, { useEffect, useState } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import './Classroom.css';
import { useTranslation } from 'react-i18next';
import { Button, Typography, Spin, Table, message, Tooltip } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

function Classroom() {
  const { t, i18n } = useTranslation();
  const [classrooms, setClassrooms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const API_URL = process.env.REACT_APP_API_URL;

  // Generate 1-hour time slots from 8:00 AM to 11:00 PM (24:00)
  const generateTimeSlots = () => {
    const slots = [];
    const startHour = 8; // 8 AM
    const endHour = 24; // 12 AM (24:00)
    
    for (let hour = startHour; hour < endHour; hour++) {
      slots.push({
        key: `${hour}:00`,
        display: `${hour.toString().padStart(2, '0')}:00`,
        startTime: `${hour.toString().padStart(2, '0')}:00`,
        hour: hour
      });
    }
    
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const formattedDate = formatDateForAPI(selectedDate);

        const [classroomResponse, classResponse] = await Promise.all([
          axios.get(`${API_URL}/classrooms`, { 
            headers: { Authorization: `Bearer ${token}` } 
          }),
          axios.get(`${API_URL}/classes/by-date?date=${formattedDate}`, { 
            headers: { Authorization: `Bearer ${token}` } 
          })
        ]);

        setClassrooms(classroomResponse.data);
        setClasses(classResponse.data);
        setError(null);

      } catch (error) {
        console.error('❌ Error fetching data:', error);
        console.error('❌ Error details:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        message.error(t('classroom.fetch_error'));
        setError(t('classroom.fetch_error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL, t, selectedDate]);

  const formatDate = (date) => {
    const options = { 
      weekday: 'long',
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    };
    return new Intl.DateTimeFormat(i18n.language, options).format(date);
  };

  // Get classes for the selected date
  const getClassEventsForDay = (date) => {
    return classes;
  };

  // Parse time string to hours
  const parseTimeToHour = (timeStr) => {
    return parseInt(timeStr.split(':')[0]);
  };

  // Find which time slots a class spans
  const findClassTimeSlots = (classStartTime, classEndTime) => {
    const startHour = parseTimeToHour(classStartTime);
    const endHour = parseTimeToHour(classEndTime);
    const endMinute = parseInt(classEndTime.split(':')[1]);
    
    // If end time has minutes, we need to include that hour too
    const actualEndHour = endMinute > 0 ? endHour + 1 : endHour;
    
    const spannedSlots = [];
    
    timeSlots.forEach((slot) => {
      if (slot.hour >= startHour && slot.hour < actualEndHour) {
        spannedSlots.push(slot.key);
      }
    });
    
    return spannedSlots;
  };

  // Sort classrooms by number
  const sortedClassrooms = [...classrooms].sort((a, b) => {
    const numA = a.number.match(/\d+/g);
    const numB = b.number.match(/\d+/g);
    return numA && numB ? parseInt(numA[0], 10) - parseInt(numB[0], 10) : a.number.localeCompare(b.number);
  });

  const truncateText = (text, maxLength = 15) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const renderDayScheduleTable = () => {
    const dayEvents = getClassEventsForDay(selectedDate);

    // Process classes by room
    const classesByRoom = new Map();
    
    dayEvents.forEach(event => {
      const roomKey = event.classroom_number;
      
      if (!classesByRoom.has(roomKey)) {
        classesByRoom.set(roomKey, []);
      }
      
      const classTimeSlots = findClassTimeSlots(
        event.schedule_time.slice(0, 5),
        event.end_time.slice(0, 5)
      );
      
      classesByRoom.get(roomKey).push({
      class_code: event.class_code,
      subject_name: event.subject_name,
      nickname: event.nickname,
      schedule_time: event.schedule_time.slice(0, 5),
      end_time: event.end_time.slice(0, 5),
      student_names: event.student_names,
      isprivate: event.isprivate,   // 👈 add this
      timeSlots: classTimeSlots,
      colSpan: classTimeSlots.length,
      startHour: parseTimeToHour(event.schedule_time.slice(0, 5))
    });
    });

    // Create columns: Room column + Time slot columns
    const columns = [
      {
        title: t('Room'),
        dataIndex: 'roomNumber',
        key: 'roomNumber',
        width: 80,
        fixed: 'left',
        onHeaderCell: () => ({
          style: {
            position: 'sticky',
            left: 0,
            zIndex: 3,
            backgroundColor: '#fafafa',
          }
        }),
        onCell: () => ({
          style: { 
            backgroundColor: '#fafafa',
            fontWeight: '600',
            textAlign: 'center',
            verticalAlign: 'top',
            fontSize: '14px',
            padding: '8px 4px',
            position: 'sticky',
            left: 0,
            zIndex: 2,
          }
        }),
        render: (text) => (
          <Text strong style={{ fontSize: '12px' }}>
            {text}
          </Text>
        ),
      },
      // Create columns for each time slot
      ...timeSlots.map(timeSlot => ({
        title: (
          <div style={{ textAlign: 'center' }}>
            <Text strong style={{ fontSize: '11px' }}>{timeSlot.display}</Text>
          </div>
        ),
        dataIndex: timeSlot.key,
        key: timeSlot.key,
        width: 60,
        onHeaderCell: () => ({
          style: {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: '#fafafa',
          }
        }),
        onCell: () => ({
          style: {
            padding: '4px !important',
            position: 'relative',
            overflow: 'visible',
            verticalAlign: 'top'
          }
        }),
  
// Updated render function inside your columns mapping
render: (text, record) => {
  const roomClasses = classesByRoom.get(record.roomNumber) || [];
  
  // Find classes that should appear in this time slot
  const classesInSlot = roomClasses.filter(classData => 
    classData.timeSlots.includes(timeSlot.key)
  );
  
  if (classesInSlot.length === 0) return null;
  
  // Calculate the required height for this cell based on stacked classes
  const maxStackIndex = Math.max(...classesInSlot.map((classData, index) => {
    const isStartingSlot = classData.timeSlots[0] === timeSlot.key;
    return isStartingSlot ? index : -1;
  }));
  
  const requiredHeight = maxStackIndex >= 0 ? (maxStackIndex + 1) * 70 + 10 : 60;
  
  return (
    <div 
      className="time-slot-container" 
       style={{ 
    position: 'relative', 
    minHeight: `${classesInSlot.length * 80}px`, 
    overflow: 'visible'
  }}
    >
      {classesInSlot.map((classData, index) => {
        // Check if this is the starting time slot for this class
        const isStartingSlot = classData.timeSlots[0] === timeSlot.key;
        
        if (!isStartingSlot) return null; // Only render in the starting slot
        
        // Define colors based on isprivate field
        const privateColors = {
          background: '#fff1f7', // Light orange background
          border: '#f5b3eb', // Orange border
          text: '#EB83DC' // Dark orange text
        };
        
        const groupColors = {
          background: '#e6f7ff', // Light blue background
          border: '#1890ff', // Blue border
          text: '#0050b3' // Dark blue text
        };
        
        // Choose colors based on isprivate field
        const isPrivate = classData.isprivate === true || classData.isprivate === 1;
        const colors = isPrivate ? privateColors : groupColors;
        
        // Create comprehensive tooltip content
        const tooltipContent = (
          <div style={{ fontSize: '15px', lineHeight: '1.4' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#1890ff' }}>
              {classData.class_code}
            </div>
            <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#EB83DC' }}>
              <strong>Subject:</strong> {classData.subject_name}
            </div>
            <div style={{ marginBottom: '4px' }}>
              <strong>Time:</strong> {classData.schedule_time} - {classData.end_time}
            </div>
            <div style={{ marginBottom: '4px' }}>
              <strong>Teacher:</strong> {classData.nickname}
            </div>
            <div style={{ marginBottom: '4px' }}>
              <strong>Room:</strong> {record.roomNumber}
            </div>
            <div style={{ marginBottom: '4px' }}>
              <strong>Type:</strong> {isPrivate ? 'Private' : 'Group'}
            </div>
            <div style={{ marginBottom: '4px' }}>
              <strong>Students:</strong>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {classData.student_names
                  ?.split(',')
                  .map((name, idx) => (
                    <li key={idx} style={{ whiteSpace: 'normal' }}>
                      {name.trim()}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        );
        
        return (
          <Tooltip title={tooltipContent} placement="top" overlayStyle={{ maxWidth: '300px' }}>
            <div
              key={`${classData.subject_name}-${classData.schedule_time}-${index}`}
              className="time-slot-item"
              style={{
                position: 'absolute',
                top: `${index * 80}px`,
                left: '2px',
                width: `calc(${classData.colSpan * 100}%)`,
                height: '75px',
                backgroundColor: colors.background,
                border: `0.5px solid ${colors.border}`,
                borderRadius: '6px',
                padding: '3px 5px',
                fontSize: '11px',
                zIndex: 10 + index,
                boxShadow: `0 2px 4px rgba(0,0,0,0.1)`,
                overflow: 'hidden',
                cursor: 'pointer',
                boxSizing: 'border-box'
              }}
            >
              <div className="item-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div className="item-header" style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                  <span className="item-time" style={{ 
                    fontSize: '13px', 
                    fontWeight: 'bold', 
                    lineHeight: '1.2',
                    color: colors.text
                  }}>
                    {classData.schedule_time} - {classData.end_time}
                  </span>
                  <span style={{ 
                    fontSize: '10px', 
                    backgroundColor: colors.border,
                    color: 'white',
                    padding: '1px 4px',
                    borderRadius: '2px',
                    fontWeight: 'bold'
                  }}>
                    {isPrivate ? 'P' : 'G'}
                  </span>
                </div>
                <div className="item-body" style={{ flex: 1, overflow: 'hidden' }}>
                  <div className="item-subject" style={{ 
                    fontSize: '13px',
                    color: colors.text, 
                    fontWeight: '600',
                    marginBottom: '2px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.3'
                  }}>
                    {truncateText(classData.class_code, classData.colSpan * 9)}
                  </div>
                  <div style={{ 
                    fontSize: '10px',
                    color: '#666', 
                    marginBottom: '1px', 
                    lineHeight: '1.2',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {truncateText(classData.nickname, classData.colSpan * 4)} | 🏫 {record.roomNumber}
                  </div>
                  <div style={{ 
                    fontSize: '10px',
                    color: '#666',
                    lineHeight: '1.2',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {truncateText(classData.student_names, classData.colSpan * 5)}
                  </div>
                </div>
              </div>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
}
      }))
    ];

    // Create data source for the table (rooms as rows)
    const dataSource = sortedClassrooms.map((classroom) => {
      const rowData = {
        key: classroom.number,
        roomNumber: classroom.number
      };

      // Initialize all time slot columns
      timeSlots.forEach(timeSlot => {
        rowData[timeSlot.key] = null;
      });

      return rowData;
    });

    return (
      <div className="day-schedule-table">
        <style>
          {`
            .ant-table-cell {
              width: 60px !important;
              min-width: 60px !important;
              max-width: 60px !important;
              padding: 4px !important;
              position: relative;
              overflow: visible !important;
              vertical-align: top !important;
            }

            .ant-table-cell:first-child {
              width: 80px !important;
              min-width: 80px !important;
              max-width: 80px !important;
            }

            .time-slot-container {
              position: relative;
              min-height: 70px;
              width: 100%;
            }

            .time-slot-item {
              position: absolute;
              z-index: 1;
              cursor: pointer;
              transition: all 0.2s ease;
            }

            .time-slot-item:hover {
              transform: translateY(-2px);
              box-shadow: 0 3px 6px rgba(0,0,0,0.15) !important;
              z-index: 20 !important;
            }

            .ant-table-tbody > tr > td {
              overflow: visible !important;
              position: relative;
            }

            .ant-table-header {
              position: sticky !important;
              top: 0;
              z-index: 2;
            }

            .ant-table-thead > tr > th {
              background-color: #fafafa;
              font-weight: 600;
              color: #1f2937;
              padding: 8px 4px;
              text-align: center;
            }

            .ant-table-thead > tr > th:first-child {
              text-align: center;
            }

            .even-room-row {
              background-color: #fafafa;
            }

            .odd-room-row {
              background-color: #ffffff;
            }

            .ant-table-tbody > tr {
              height: auto !important;
            }

            .ant-table-tbody > tr > td {
              height: auto !important;
              vertical-align: top !important;
            }
          `}
        </style>
        <Table 
          dataSource={dataSource} 
          columns={columns} 
          pagination={false}
          bordered
          size="small"
          scroll={{ x: 'max-content' }}
          className="schedule-table"
          rowClassName={(record, index) => 
            index % 2 === 0 ? 'even-room-row' : 'odd-room-row'
          }
          sticky={{
            offsetHeader: 0,
            offsetScroll: 0,
            getContainer: () => window
          }}
        />
      </div>
    );
  };

  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const isToday = () => {
    const today = new Date();
    const selected = new Date(selectedDate);
    return today.toDateString() === selected.toDateString();
  };

  if (loading) return <Spin tip={t('loading')} size="large" style={{ 
    position: 'fixed', 
    top: '50%', 
    left: '50%', 
    transform: 'translate(-50%, -50%)' 
  }} />;
  
  if (error) return (
    <div style={{ textAlign: 'center', marginTop: '50px' }}>
      <Text type="danger" style={{ fontSize: '16px' }}>{error}</Text>
    </div>
  );

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="classroom-page-container" >
        {/* Date Navigation */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: '16px', 
          marginBottom: '10px',
          background: '#fafafa',
          padding: '16px',
          borderRadius: '8px'
        }}>
          <Button
            onClick={handlePreviousDay}
            icon={<LeftOutlined />}
            style={{ fontSize: '16px' }}
          >
            {t('Yesterday')}
          </Button>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            minWidth: '300px'
          }}>
            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
              {formatDate(selectedDate)}
            </Title>
            {!isToday() && (
              <Button
                type="link"
                onClick={handleToday}
                style={{ padding: '0', height: 'auto', fontSize: '14px' }}
              >
                {t('Go To Today')}
              </Button>
            )}
          </div>
          
          <Button
            onClick={handleNextDay}
            icon={<RightOutlined />}
            iconPosition="end"
            style={{ fontSize: '16px' }}
          >
            {t('Tomorrow')}
          </Button>
        </div>

        {/* Schedule Table */}
        <div style={{ 
          background: 'white', 
          borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          position: 'relative'
        }}>
          {loading ? (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center',
              minHeight: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Spin size="large" tip={t('loading')} />
            </div>
          ) : (
            renderDayScheduleTable()
          )}
        </div>
      </div>
    </div>
  );
}

export default withRoleAccess(Classroom, ['admin', 'superadmin']);